import { ChangeDetectorRef, Component, forwardRef, OnDestroy, inject } from '@angular/core';
import {
  FormBuilder,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  UntypedFormGroup,
  ValidationErrors,
  Validator,
  Validators,
} from '@angular/forms';
import { environment } from '@env/environment.stg';
import { AbstractControlValueAccessor } from '../../abstract_control_value_accessor.abstract';
import { Subscription } from 'rxjs';

import { TranslatePipe } from '@ngx-translate/core';
import { AppFocusableDirective } from '@app/shared/directives/focusable.directive';
import { InputErrorsComponent } from '@app/shared/components/input-errors/input-errors.component';

@Component({
  selector: 'app-password',
  templateUrl: './password.component.html',
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => PasswordComponent), multi: true },
    { provide: NG_VALIDATORS, useExisting: forwardRef(() => PasswordComponent), multi: true },
  ],
  imports: [
    ReactiveFormsModule,
    TranslatePipe,
    AppFocusableDirective,
    InputErrorsComponent
],
})
export class PasswordComponent extends AbstractControlValueAccessor implements Validator, OnDestroy {
  private formBuilder = inject(FormBuilder);
  private cdr = inject(ChangeDetectorRef);

  public form = this.formBuilder.group({
    password: ['', [Validators.required, Validators.minLength(environment.passwordMinLength)]],
    password_confirmation: ['', Validators.required],
  }, {
    validator: PasswordComponent.samePassword,
  });

  private formSub: Subscription;

  public static samePassword(formGroup: UntypedFormGroup): Record<string, boolean> | null {
    if (formGroup.get('password')?.value !== formGroup.get('password_confirmation')?.value) {
      return { password_mismatch: true };
    }
    return null;
  }

  constructor() {
    super();
    this.formSub = this.form.valueChanges.subscribe(() => this.onFormChanged());
  }

  public ngOnDestroy(): void {
    this.formSub.unsubscribe();
  }

  public writeValue(): null {
    return null;
  }

  public validate(): ValidationErrors | any {
    const errors: Record<string, unknown> = {};

    const passwordErrors = this.form.controls.password.errors;
    if (passwordErrors?.required) {
      errors.password = {
        property: 'password',
        error: 'required',
      };
    }

    if (passwordErrors?.minlength) {
      errors.password = {
        property: 'password',
        error: 'minlength',
        requiredLength: passwordErrors.minlength.requiredLength, // { "requiredLength": 8, "actualLength": 3 }
      };
    }

    if (this.form.errors) {
      errors.password_confirmation = {
        property: 'password_confirmation',
        error: 'password_mismatch',
      };
    } else {
      if (this.form.controls.password_confirmation.errors?.required) {
        errors.password_confirmation = {
          property: 'password_confirmation',
          error: 'required',
        };
      }
    }

    const result = Object.keys(errors) ? errors : null;
    this.form.setErrors(result);

    this.cdr.detectChanges();
    return result;
  }

  private onFormChanged(): void {
    this.onTouched();
    this.onChange(this.form.get('password')?.value);
  }
}
